<template>
  <section class="home-hero">
    <div class="hero-bg" aria-hidden="true">
      <span class="orb orb-1"></span>
      <span class="orb orb-2"></span>
      <span class="orb orb-3"></span>
    </div>

    <div class="container hero-inner">
      <div class="hero-copy">
        <p class="hero-eyebrow anim-fade-up" style="animation-delay: 0.05s">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M12 2l8 4.5v9L12 20l-8-4.5v-9L12 2z" />
            <circle cx="12" cy="12" r="2.4" />
          </svg>
          HOLINK TECH · 软件定制开发服务商
        </p>

        <h1 class="hero-title anim-fade-up" style="animation-delay: 0.15s">
          让每一个想法<br />
          <span class="grad">都能被交付</span>
        </h1>

        <p class="hero-sub anim-fade-up" style="animation-delay: 0.28s">
          浩连科技专注于小程序 / App / 网站 / 系统定制开发，<br />
          从需求分析到上线交付，为企业提供一站式数字化解决方案。
        </p>

        <div class="hero-actions anim-fade-up" style="animation-delay: 0.4s">
          <router-link to="/contact" class="btn btn-primary">
            免费获取方案
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </router-link>
          <router-link to="/cases" class="btn btn-outline">查看成功案例</router-link>
        </div>

        <ul class="hero-tags anim-fade-up" style="animation-delay: 0.5s">
          <li>微信小程序</li>
          <li>iOS / Android</li>
          <li>企业官网</li>
          <li>管理系统</li>
        </ul>
      </div>

      <div class="hero-visual anim-fade-up" style="animation-delay: 0.35s" aria-hidden="true">
        <div class="glass-card main-card">
          <div class="g-card-head">
            <span class="g-dot"></span><span class="g-dot"></span><span class="g-dot"></span>
          </div>
          <div class="g-chart">
            <div class="g-bar" style="height: 34%"></div>
            <div class="g-bar" style="height: 58%"></div>
            <div class="g-bar" style="height: 44%"></div>
            <div class="g-bar" style="height: 76%"></div>
            <div class="g-bar" style="height: 62%"></div>
            <div class="g-bar" style="height: 90%"></div>
            <div class="g-bar" style="height: 70%"></div>
          </div>
          <div class="g-meta">
            <span class="num">120+</span>
            <span>项目成功交付</span>
          </div>
        </div>

        <div class="glass-card float-card fc-1">
          <span class="fc-icon">✓</span>
          <div>
            <b class="num">98%</b>
            <small>客户满意度</small>
          </div>
        </div>

        <div class="glass-card float-card fc-2">
          <span class="fc-icon">⚡</span>
          <div>
            <b class="num">15天</b>
            <small>平均交付周期</small>
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="hero-stats anim-fade-up" style="animation-delay: 0.6s">
        <StatCounter
          v-for="s in site.stats"
          :key="s.label"
          :value="s.value"
          :suffix="s.suffix"
          :label="s.label"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import site from '@/data/site'
import StatCounter from '@/components/StatCounter.vue'
</script>

<style scoped>
.home-hero {
  position: relative;
  background: linear-gradient(165deg, var(--hl-dark) 0%, #0d2a52 48%, #0d3677 100%);
  padding: calc(var(--nav-h) + 72px) 0 40px;
  overflow: hidden;
}

/* 背景网格 */
.hero-bg {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: gridMove 14s linear infinite;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}

.orb-1 {
  width: 460px;
  height: 460px;
  top: -140px;
  right: -80px;
  background: radial-gradient(circle, rgba(53, 194, 247, 0.4), transparent 65%);
  animation: pulseGlow 7s ease-in-out infinite;
}

.orb-2 {
  width: 380px;
  height: 380px;
  bottom: -60px;
  left: -120px;
  background: radial-gradient(circle, rgba(11, 92, 255, 0.5), transparent 65%);
  animation: pulseGlow 9s ease-in-out infinite;
  animation-delay: 1.2s;
}

.orb-3 {
  width: 240px;
  height: 240px;
  top: 30%;
  left: 42%;
  background: radial-gradient(circle, rgba(53, 194, 247, 0.22), transparent 65%);
  animation: pulseGlow 6s ease-in-out infinite;
  animation-delay: 2s;
}

.hero-inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: 56px;
  min-height: calc(100vh - var(--nav-h) - 220px);
  padding-top: 40px;
  padding-bottom: 80px;
}

/* 文案区 */
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 2px;
  color: var(--hl-cyan);
}

.hero-eyebrow svg {
  width: 18px;
  height: 18px;
}

.hero-title {
  margin-top: 22px;
  font-size: 62px;
  font-weight: 800;
  line-height: 1.22;
  color: #fff;
  letter-spacing: 1px;
}

.hero-title .grad {
  background: linear-gradient(90deg, #5fb4ff, var(--hl-cyan) 60%, #7fe0ff);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-sub {
  margin-top: 24px;
  font-size: 18px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.72);
}

.hero-actions {
  margin-top: 36px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.hero-actions svg {
  width: 18px;
  height: 18px;
}

.hero-tags {
  margin-top: 34px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.hero-tags li {
  padding: 6px 14px;
  border-radius: 30px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

/* 视觉区 */
.hero-visual {
  position: relative;
  height: 440px;
}

.glass-card {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(5, 15, 35, 0.4);
}

.main-card {
  position: absolute;
  inset: 26px 10px 30px 30px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  animation: floatY 7s ease-in-out infinite;
}

.g-card-head {
  display: flex;
  gap: 7px;
}

.g-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.28);
}

.g-chart {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 26px 6px 10px;
}

.g-bar {
  flex: 1;
  border-radius: 6px 6px 2px 2px;
  background: linear-gradient(180deg, var(--hl-cyan), var(--hl-blue));
  opacity: 0.85;
  animation: pulseGlow 4s ease-in-out infinite;
}

.g-bar:nth-child(2n) {
  animation-delay: 0.8s;
}

.g-meta {
  display: flex;
  align-items: baseline;
  gap: 10px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.g-meta span.num {
  font-size: 30px;
  font-weight: 700;
  color: var(--hl-cyan);
}

.float-card {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
}

.fc-1 {
  top: 0;
  right: 0;
  animation: floatY 6s ease-in-out infinite;
}

.fc-2 {
  bottom: 0;
  left: 0;
  animation: floatY 6.5s ease-in-out infinite;
  animation-delay: 1s;
}

.fc-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 18px;
  background: linear-gradient(135deg, var(--hl-blue), var(--hl-cyan));
  color: #fff;
}

.float-card b {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.float-card small {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
}

/* 底部统计条 */
.hero-stats {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px;
  backdrop-filter: blur(10px);
  padding: 28px 12px;
  margin-bottom: 36px;
}

/* 响应式 */
@media (max-width: 992px) {
  .hero-inner {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-bottom: 56px;
  }
  .hero-visual {
    height: 380px;
    max-width: 520px;
    width: 100%;
    margin: 0 auto;
  }
  .hero-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px 0;
  }
  .hero-title {
    font-size: 46px;
  }
}

@media (max-width: 768px) {
  .home-hero {
    padding: calc(var(--nav-h) + 40px) 0 24px;
  }
  .hero-title {
    font-size: 36px;
  }
  .hero-sub {
    font-size: 15px;
  }
  .hero-visual {
    height: 320px;
  }
  .main-card {
    inset: 20px 6px 22px 16px;
  }
  .g-meta span.num {
    font-size: 24px;
  }
  .fc-icon {
    width: 34px;
    height: 34px;
  }
  .float-card b {
    font-size: 16px;
  }
}
</style>
